import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Container,
  Eyebrow,
  SectionTitle,
  Lede,
  Card,
} from '../AumhumSite/sections'
import {
  CheckCircle2,
  MessageCircle,
  PhoneCall,
  ShieldCheck,
} from '../AumhumSite/icons'
import {
  fetchMyProspectBundle,
  isLoggedIn,
  PROSPECT_MOCK_MODE,
} from 'src/consumer/services/patientProspectService'
import {
  PROSPECT_DRAFT_KEY,
  PROSPECT_DRAFT_TTL_MS,
} from '../PatientProspectSignup/constants'
import CareStatusIntakeForm from './CareStatusIntakeForm'
import '../PatientProspectSignup/PatientProspectSignup.css'
import './CareStatusIntake.css'

// /care/status — the post-login dashboard for ROLE_PATIENT_PROSPECT users.
// Renders three sections in order:
//   1. Status banner (submitted / pathway recap / contact card)
//   2. Recommendation card if the triage engine has produced one
//   3. Progressive intake form (collapsible sections + reports upload),
//      backed by PATCH /api/secure/me/patient-prospect/intake and
//      POST /api/secure/me/patient-prospect/reports.
//
// Data flow: `fetchMyProspectBundle` returns the authoritative server-side
// bundle in real mode (or the mock-mode equivalent constructed from the
// signup mock session). The pre-signup draft in localStorage is used only
// as a fallback for the very brief window between signup completing and
// the first server fetch.

function readDraft() {
  try {
    const raw = window.localStorage.getItem(PROSPECT_DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > PROSPECT_DRAFT_TTL_MS) {
      window.localStorage.removeItem(PROSPECT_DRAFT_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const FALLBACK_CONTACT = {
  whatsapp: 'https://wa.me/918047492952',
  phone: '+918047492952',
  chatHandle: 'aumhum-care',
}

const PATHWAY_LABEL = {
  PSYCHIATRY: 'Mental health',
  THERAPY: 'Mental health',
  BIOLOGICAL: 'Biological',
  CHILD: 'Child growth & care',
  ASSESSMENT: 'Not sure / something else',
}
const SPECIALTY_TOP_LABEL = {
  endocrinology: 'Hormonal health',
  rheumatology: 'Immune system',
}
const SPECIALTY_SUB_LABEL = {
  psychiatry: 'Psychiatry',
  psychology: 'Therapy / Psychology',
}
function humaniseTopLabel(pathway, specialtyTag) {
  if (specialtyTag && SPECIALTY_TOP_LABEL[specialtyTag]) return SPECIALTY_TOP_LABEL[specialtyTag]
  if (pathway && PATHWAY_LABEL[pathway]) return PATHWAY_LABEL[pathway]
  return 'Healthcare'
}
function humaniseSubLabel(specialtyTag) {
  if (!specialtyTag) return null
  return SPECIALTY_SUB_LABEL[specialtyTag] || null
}

function deriveRecap(bundle, draft) {
  const p = bundle?.prospect
  if (p?.pathway || p?.specialtyTag) {
    return {
      topLabel: humaniseTopLabel(p.pathway, p.specialtyTag),
      subLabel: humaniseSubLabel(p.specialtyTag),
    }
  }
  if (draft?.resolved) return draft.resolved
  return null
}

function deriveFirstName(bundle) {
  const name = bundle?.prospect?.name
  if (!name) return 'there'
  return name.split(' ')[0]
}

export default function CareStatusView() {
  const [bundle, setBundle] = useState(null)
  const [draft, setDraft] = useState(null)
  const [loadingBundle, setLoadingBundle] = useState(false)
  const signedIn = useMemo(isLoggedIn, [])
  const [searchParams] = useSearchParams()
  // MobileOtpStep redirects here with `?initFailed=1&reason=…` when the
  // user successfully verified their OTP (so they ARE signed in) but the
  // /me/patient-prospect/initialize call failed afterwards. Without this
  // notice the user lands on the "No active care request found" empty
  // state with no idea why their care match didn't go through.
  const initFailed = searchParams.get('initFailed') === '1'
  const initFailReason = searchParams.get('reason') || ''

  useEffect(() => {
    setDraft(readDraft())
    if (PROSPECT_MOCK_MODE || signedIn) {
      setLoadingBundle(true)
      fetchMyProspectBundle()
        .then((b) => setBundle(b))
        .catch(() => setBundle(null))
        .finally(() => setLoadingBundle(false))
    }
  }, [signedIn])

  const recap = deriveRecap(bundle, draft)
  const reco = bundle?.recommendation
  const firstName = deriveFirstName(bundle)
  const submittedAt = bundle?.prospect?.createdAt
  const hasProspectData = !!bundle?.prospect

  // Empty state — neither a server bundle nor a usable draft.
  if (!hasProspectData && !recap) {
    return (
      <Container className="pps-shell">
        <Eyebrow>Care status</Eyebrow>
        <SectionTitle as="h1">
          {initFailed
            ? 'You’re signed in. We just couldn’t finish the matching step.'
            : 'No active care request found'}
        </SectionTitle>
        <Lede>
          {initFailed
            ? 'Your account is set up, but the request to match you with a specialist didn’t go through. Try once more — it only takes a minute.'
            : 'To get matched with a specialist, start a new request — it only takes a minute.'}
        </Lede>
        {initFailed && initFailReason ? (
          <div
            className="pps-recap"
            role="status"
            style={{ marginTop: '0.75rem', borderColor: 'var(--slate-200)' }}
          >
            <strong>Why it didn&rsquo;t finish: </strong>{initFailReason}
          </div>
        ) : null}
        <div className="pps-cta-row">
          <Link to="/care/get-started" className="pps-btn is-primary">
            {initFailed ? 'Try matching again' : 'Start a new request'}
          </Link>
        </div>
        {loadingBundle ? (
          <div className="pps-help" style={{ marginTop: '1rem' }}>Loading the latest from your care team…</div>
        ) : null}
      </Container>
    )
  }

  // Soft-revoke / closed-request state (per D3 in the plan): the user
  // keeps ROLE_PATIENT_PROSPECT but the request was closed by support
  // (PatientProspectInfo.status === 'CLOSED') or the prospect is now a
  // patient (status === 'PROMOTED' — they should usually be on
  // /patient-home/* but we surface a friendly stub here in case they end
  // up on /care/status). Shows softer copy + still offers contact.
  const closedStatuses = new Set(['CLOSED'])
  const promotedStatuses = new Set(['PROMOTED'])
  const prospectStatus = bundle?.prospect?.status
  if (closedStatuses.has(prospectStatus)) {
    return (
      <Container className="pps-shell">
        <Eyebrow>Your care request</Eyebrow>
        <SectionTitle as="h1">
          {firstName === 'there'
            ? 'Thanks for reaching out.'
            : `${firstName}, thanks for reaching out.`}
        </SectionTitle>
        <Lede>
          We weren&rsquo;t able to take this particular request forward, but we&rsquo;re still
          here whenever you need us. You can chat with our care team anytime, or start a
          fresh request whenever you&rsquo;re ready.
        </Lede>

        <Card className="pps-success-card" style={{ borderColor: 'var(--slate-200)' }}>
          <div className="pps-contact-row">
            <a className="pps-contact-btn" href={FALLBACK_CONTACT.whatsapp} target="_blank" rel="noreferrer">
              <MessageCircle /> WhatsApp
            </a>
            <a className="pps-contact-btn" href={`tel:${FALLBACK_CONTACT.phone}`}>
              <PhoneCall /> Call us
            </a>
          </div>
        </Card>

        <div className="pps-cta-row" style={{ marginTop: '1rem' }}>
          <Link to="/care/get-started" className="pps-btn is-primary">Start a new request</Link>
        </div>

        <div className="pps-recap" style={{ marginTop: '1.25rem' }}>
          <ShieldCheck style={{ verticalAlign: '-3px', marginRight: 6 }} />
          We keep your information confidential. Reaching out again is always welcome.
        </div>
      </Container>
    )
  }

  if (promotedStatuses.has(prospectStatus)) {
    return (
      <Container className="pps-shell">
        <Eyebrow>Your care request</Eyebrow>
        <SectionTitle as="h1">
          {firstName === 'there'
            ? 'You’re matched with a specialist.'
            : `${firstName}, you’re matched with a specialist.`}
        </SectionTitle>
        <Lede>
          Your request has moved forward and a clinic is now coordinating your care.
          Find your bookings, prescriptions, and updates in your patient home.
        </Lede>
        <div className="pps-cta-row">
          <Link to="/patient-home" className="pps-btn is-primary">Open patient home</Link>
        </div>
        <Card className="pps-success-card" style={{ marginTop: '1rem' }}>
          <div className="pps-contact-row">
            <a className="pps-contact-btn" href={FALLBACK_CONTACT.whatsapp} target="_blank" rel="noreferrer">
              <MessageCircle /> WhatsApp
            </a>
            <a className="pps-contact-btn" href={`tel:${FALLBACK_CONTACT.phone}`}>
              <PhoneCall /> Call us
            </a>
          </div>
        </Card>
      </Container>
    )
  }

  return (
    <Container className="pps-shell">
      <Eyebrow>Your care request</Eyebrow>
      <SectionTitle as="h1">
        {firstName === 'there'
          ? 'We’re finding the right specialist for you.'
          : `Hi ${firstName} — we’re finding the right specialist for you.`}
      </SectionTitle>

      <Card className="pps-success-card" emphasised>
        <span className="pps-success-status">
          <CheckCircle2 /> Submitted{submittedAt ? ` · ${new Date(submittedAt).toLocaleString()}` : ''}
        </span>
        {recap ? (
          <div>
            You picked <strong>{recap.topLabel}
              {recap.subLabel ? <> &mdash; {recap.subLabel}</> : null}</strong>.
          </div>
        ) : null}
        <Lede>
          A care coordinator will reach out within 24 hours. Meanwhile, you can chat with
          us right away.
        </Lede>
        <div className="pps-contact-row">
          <a className="pps-contact-btn" href={FALLBACK_CONTACT.whatsapp} target="_blank" rel="noreferrer">
            <MessageCircle /> WhatsApp
          </a>
          <a className="pps-contact-btn" href={`tel:${FALLBACK_CONTACT.phone}`}>
            <PhoneCall /> Call us
          </a>
        </div>
      </Card>

      {reco ? (
        <Card className="pps-card-form" style={{ marginTop: '1rem' }}>
          <Eyebrow>Initial recommendation</Eyebrow>
          <SectionTitle as="h2">{reco.userFacingSummary}</SectionTitle>
          {reco.rationale ? <Lede>{reco.rationale}</Lede> : null}
        </Card>
      ) : null}

      {hasProspectData ? (
        <div style={{ marginTop: '1rem' }}>
          <CareStatusIntakeForm bundle={bundle} onBundleChange={setBundle} />
        </div>
      ) : null}

      <div className="pps-recap" style={{ marginTop: '1.25rem' }}>
        <ShieldCheck style={{ verticalAlign: '-3px', marginRight: 6 }} />
        Your details are encrypted and shared only with the assigned care team.
      </div>

      {loadingBundle ? (
        <div className="pps-help" style={{ marginTop: '1rem' }}>Loading the latest from your care team…</div>
      ) : null}

      {PROSPECT_MOCK_MODE ? (
        <div className="pps-help" style={{ marginTop: '1rem' }}>
          (Phase 1 mock session. Backend wiring lands in Phase 2.)
        </div>
      ) : null}
    </Container>
  )
}
